import React, { useState } from "react";
import { Helmet } from "react-helmet";
import NavBar from "../Components/NavBar/NavBar";
import {
    Container,
    Form,
    Row,
    Col,
    Button,
    FloatingLabel,
    Dropdown
} from "react-bootstrap";
import Footer from "../Components/Footer/Footer";
import { FaShieldAlt, FaLock, FaUniversity } from "react-icons/fa";

const Register = () => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        dob: "",
        idType: "",
        idNumber: "",
        passportNumber: "",
        billNumber: "",
        emiPlan: "",
        bankAccountType: ""
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const bankAccountTypes = [
        { value: "SAVINGS", label: "Savings Account" },
        { value: "CURRENT", label: "Current Account" },
        { value: "JOINT", label: "Joint Account" }
    ];

    const idTypes = [
        { value: "NRIC", label: "NRIC" },
        { value: "FIN", label: "FIN" },
        { value: "PASSPORT", label: "Passport" }
    ];

    const emiPlans = [
        { value: "3", label: "3 Months (0% interest)" },
        { value: "6", label: "6 Months (1.5% interest)" },
        { value: "12", label: "12 Months (3% interest)" }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleSelect = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const validateStep = (step) => {
        const newErrors = {};

        if (step === 1) {
            if (!formData.fullName.trim()) newErrors.fullName = "Required";
            if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = "Invalid email";
            if (!formData.phone.match(/^[\d\s+-]{8,}$/)) newErrors.phone = "Invalid number";
        }

        if (step === 2) {
            if (!formData.idType) newErrors.idType = "Required";
            if (!formData.idNumber.trim()) newErrors.idNumber = "Required";
            if (!formData.bankAccountType) newErrors.bankAccountType = "Required";
        }

        if (step === 3) {
            if (!formData.billNumber.trim()) newErrors.billNumber = "Required";
            if (!formData.emiPlan) newErrors.emiPlan = "Required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmit = (e) => {
        console.log(formData)
        e.preventDefault();
        if (validateStep(3)) {
            setIsSubmitting(true);
            setTimeout(() => {
                setIsSubmitting(false);
                setSubmitSuccess(true);
            }, 1500);
        }
    };

    const renderStepIndicator = () => (
        <div className="d-flex justify-content-center align-items-center mb-4">
            {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                    <div
                        className={`d-flex align-items-center justify-content-center rounded-circle ${currentStep === step ? 'bg-primary text-white' : 'bg-light'} `}
                        style={{ width: '36px', height: '36px' }}
                    >
                        {step}
                    </div>
                    {step < 3 && (
                        <div className={`mx-2 ${currentStep > step ? 'text-primary' : 'text-muted'}`}>
                            <i className="bi bi-chevron-right"></i>
                        </div>
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    const renderPersonalInfoStep = () => (
        <>
            <h2 className="h5 mb-4 text-primary">
                <i className="bi bi-person-lines-fill me-2"></i>
                Personal Information
            </h2>

            <Row className="g-3 mb-3">
                <Col md={6}>
                    <FloatingLabel controlId="fullName" label="Full Name *">
                        <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            isInvalid={!!errors.fullName}
                            placeholder=" "
                            className="border-2"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.fullName}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Col>
                <Col md={6}>
                    <FloatingLabel controlId="email" label="Email *">
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder=" "
                            className="border-2"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.email}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col md={6}>
                    <FloatingLabel controlId="phone" label="Phone Number *">
                        <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            isInvalid={!!errors.phone}
                            placeholder=" "
                            className="border-2"
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.phone}
                        </Form.Control.Feedback>
                    </FloatingLabel>
                </Col>
                <Col md={6}>
                    <FloatingLabel controlId="dob" label="Date of Birth">
                        <Form.Control
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            placeholder=" "
                            className="border-2"
                        />
                    </FloatingLabel>
                </Col>
            </Row>
        </>
    );

    const renderBankDetailsStep = () => (
        <>
            <h2 className="h5 mb-4 text-primary">
                <i className="bi bi-credit-card me-2"></i>
                Bank Details
            </h2>

            <Form.Group className="mb-3">
                <Form.Label>Bank Account Type *</Form.Label>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        id="account-type-dropdown"
                        className={`w-100 text-start ${errors.bankAccountType ? 'is-invalid' : ''}`}
                    >
                        {formData.bankAccountType
                            ? bankAccountTypes.find(t => t.value === formData.bankAccountType)?.label
                            : "Select account type"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                        {bankAccountTypes.map(type => (
                            <Dropdown.Item
                                key={type.value}
                                onClick={() => handleSelect('bankAccountType', type.value)}
                            >
                                {type.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                    {errors.bankAccountType && (
                        <div className="invalid-feedback d-block">
                            {errors.bankAccountType}
                        </div>
                    )}
                </Dropdown>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>ID Type *</Form.Label>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        id="id-type-dropdown"
                        className={`w-100 text-start ${errors.idType ? 'is-invalid' : ''}`}
                    >
                        {formData.idType
                            ? idTypes.find(t => t.value === formData.idType)?.label
                            : "Select ID type"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                        {idTypes.map(type => (
                            <Dropdown.Item
                                key={type.value}
                                onClick={() => handleSelect('idType', type.value)}
                            >
                                {type.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                    {errors.idType && (
                        <div className="invalid-feedback d-block">
                            {errors.idType}
                        </div>
                    )}
                </Dropdown>
            </Form.Group>

            <FloatingLabel controlId="idNumber" label="ID Number *" className="mb-3">
                <Form.Control
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.idNumber}
                    placeholder=" "
                    className="border-2"
                />
                <Form.Control.Feedback type="invalid">
                    {errors.idNumber}
                </Form.Control.Feedback>
            </FloatingLabel>
        </>
    );

    const renderPaymentDetailsStep = () => (
        <>
            <h2 className="h5 mb-4 text-primary">
                <i className="bi bi-receipt me-2"></i>
                Payment Details
            </h2>

            <Form.Group className="mb-3">
                <Form.Label>Payment Plan *</Form.Label>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        id="emi-plan-dropdown"
                        className={`w-100 text-start ${errors.emiPlan ? 'is-invalid' : ''}`}
                    >
                        {formData.emiPlan
                            ? emiPlans.find(p => p.value === formData.emiPlan)?.label
                            : "Select payment plan"}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                        {emiPlans.map(plan => (
                            <Dropdown.Item
                                key={plan.value}
                                onClick={() => handleSelect('emiPlan', plan.value)}
                            >
                                {plan.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                    {errors.emiPlan && (
                        <div className="invalid-feedback d-block">
                            {errors.emiPlan}
                        </div>
                    )}
                </Dropdown>
            </Form.Group>

            <FloatingLabel controlId="billNumber" label="Invoice/Bill Number *" className="mb-4">
                <Form.Control
                    type="text"
                    name="billNumber"
                    value={formData.billNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.billNumber}
                    placeholder=" "
                    className="border-2"
                />
                <Form.Control.Feedback type="invalid">
                    {errors.billNumber}
                </Form.Control.Feedback>
            </FloatingLabel>
        </>
    );

    const renderFormSteps = () => {
        switch (currentStep) {
            case 1:
                return renderPersonalInfoStep();
            case 2:
                return renderBankDetailsStep();
            case 3:
                return renderPaymentDetailsStep();
            default:
                return null;
        }
    };

    const renderNavigationButtons = () => (
        <div className="d-flex justify-content-between mt-4">
            {currentStep > 1 ? (
                <Button variant="outline-secondary" onClick={handlePrev}>
                    Previous
                </Button>
            ) : (
                <div></div>
            )}

            {currentStep < 3 ? (
                <Button variant="primary" onClick={handleNext}>
                    Next
                </Button>
            ) : (
                <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                        </>
                    ) : (
                        "Authorize GIRO Payments"
                    )}
                </Button>
            )}
        </div>
    );

    return (
        <div className="d-flex flex-column min-vh-100 bg-light">
            <Helmet>
                <title>GIRO Registration – AZZ Enterprises</title>
            </Helmet>
            <NavBar />

            <div className="bg-primary text-white py-5 shadow-sm">
                <Container className="d-flex flex-column align-items-center text-center">
                    <h1 className="fw-bold display-6 mb-2">GIRO Registration</h1>
                    <p className="lead text-white-50 mb-4">
                        Authorize your bank to process EMI payments securely
                    </p>

                    {/* Trust + Security Highlights */}
                    <div className="row justify-content-center text-start w-100 mt-4">
                        <div className="col-md-4 mb-3">
                            <div className="d-flex align-items-start">
                                <FaShieldAlt size={28} className="me-3 text-white" />
                                <div>
                                    <h6 className="fw-bold text-white mb-1">Secure & Verified</h6>
                                    <p className="small text-white-50 mb-0">
                                        We implement strong encryption and secure protocols for every submission.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <div className="d-flex align-items-start">
                                <FaLock size={28} className="me-3 text-white" />
                                <div>
                                    <h6 className="fw-bold text-white mb-1">Privacy First</h6>
                                    <p className="small text-white-50 mb-0">
                                        Your personal data is handled with the highest level of confidentiality.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <div className="d-flex align-items-start">
                                <FaUniversity size={28} className="me-3 text-white" />
                                <div>
                                    <h6 className="fw-bold text-white mb-1">Bank-Grade Trust</h6>
                                    <p className="small text-white-50 mb-0">
                                        Our partnership with DBS ensures reliability and efficiency for all EMI transactions.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </div>

            <Container className="flex-grow-1 py-4">
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        {submitSuccess ? (
                            <div className="text-center p-4 bg-white rounded shadow-sm">
                                <div className="text-success mb-3">
                                    <i className="bi bi-check-circle-fill fs-1"></i>
                                </div>
                                <h2 className="h4 mb-3">Submission Received</h2>
                                <p className="mb-4">
                                    Your GIRO authorization request has been successfully submitted.
                                    A confirmation will be sent to your email within 24 hours.
                                </p>
                                <Button
                                    variant="outline-primary"
                                    onClick={() => {
                                        setSubmitSuccess(false);
                                        setFormData({
                                            fullName: "",
                                            email: "",
                                            phone: "",
                                            dob: "",
                                            idType: "",
                                            idNumber: "",
                                            passportNumber: "",
                                            billNumber: "",
                                            emiPlan: "",
                                            bankAccountType: ""
                                        });
                                        setCurrentStep(1);
                                    }}
                                >
                                    Start New Submission
                                </Button>
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm">
                                {renderStepIndicator()}
                                {renderFormSteps()}
                                {renderNavigationButtons()}
                                {currentStep === 3 && (
                                    <p className="text-muted small mt-3 text-center">
                                        By submitting, you authorize AZZ Enterprises to debit your account according to the selected terms.
                                    </p>
                                )}
                            </Form>
                        )}
                    </Col>
                </Row>
            </Container>

            <Footer />
        </div>
    );
};

export default Register;